<template>
    <div class="marks" aria-hidden="true">
        <span v-for="mark in MARKS" :key="mark.key" ref="markEls" class="mark" :style="{ color: mark.color }">
            <component :is="mark.component" />
        </span>
    </div>
</template>

<script setup lang="ts">
/**
 * The collaboration profile's own six section marks, animated. They fall in left to right, hold, then each
 * turns a whole revolution in sequence before the wave runs again. Using the profile's marks rather than an
 * abstract shape is what ties the illustration to what is being written.
 *
 * The bounce is the Perspective mark's `pmarkDrop` keyframes replayed here property by property, so the drop
 * and the wave that follows share one clock. Left to CSS, each mark's animation would start whenever its
 * element happened to be styled, and the wave would have nothing exact to follow from.
 *
 * Values are written per frame rather than by CSS transition, for the reason CalendarWeekLoader gives: a
 * throttled tab leaves transitions stuck and the loader looking broken.
 */
import { COLLAB_ICONS } from "~vue/utils/collabIcons";
import { onBeforeUnmount, onMounted, useTemplateRef } from "vue";

/* The profile's sections in the order they are written, so the row reads as the document it is drafting.
   Colours come with the marks, which is why no palette is named here. */
const MARKS = [
    { key: "communicationStyle", ...COLLAB_ICONS.communicationStyle },
    { key: "workStructure", ...COLLAB_ICONS.workStructure },
    { key: "decisionMaking", ...COLLAB_ICONS.decisionMaking },
    { key: "informationProcessing", ...COLLAB_ICONS.informationProcessing },
    { key: "feedbackCollaboration", ...COLLAB_ICONS.feedbackCollaboration },
    { key: "motivatorsStressors", ...COLLAB_ICONS.motivatorsStressors },
];

/** One mark's fall, and how far behind the one to its left it starts. */
const DROP_MS = 720;
const DROP_STAGGER = 100;
/** Everything landed, before the first wave. */
const HOLD_MS = 420;
/** One mark's revolution, and how far behind the one to its left it turns. */
const SPIN_MS = 700;
const SPIN_STAGGER = 120;
/** The beat the row rests on between waves. */
const WAVE_PAUSE = 900;

const LAST_DROP_ENDS = DROP_MS + DROP_STAGGER * (MARKS.length - 1);
const WAVE_STARTS = LAST_DROP_ENDS + HOLD_MS;
const WAVE_CYCLE = SPIN_MS + SPIN_STAGGER * (MARKS.length - 1) + WAVE_PAUSE;

type Curve = [number, number, number, number];

/** A keyframe: where in the drop it sits, the value there, and the easing CSS applies from it onwards. */
type Keyframe = [number, number, Curve | null];

/* The CSS "standard" curve and the one `pmarkDrop` accelerates into. */
const STANDARD: Curve = [0.33, 0, 0.67, 1];
const IN: Curve = [0.55, 0.06, 0.68, 0.19];

/* `pmarkDrop` split into one track per animated property, because a transform is written as a whole string
   and cannot be interpolated the way CSS interpolates a keyframe. */
const TRACKS: Record<"y" | "scaleX" | "scaleY" | "opacity", Keyframe[]> = {
    y: [
        [0, -11, IN],
        [0.45, 0, STANDARD],
        [0.62, -3.2, IN],
        [0.78, 0, STANDARD],
        [0.9, -1.1, null],
        [1, 0, null],
    ],
    scaleX: [
        [0, 0.92, IN],
        [0.45, 1.12, STANDARD],
        [0.62, 1, IN],
        [0.78, 1.06, STANDARD],
        [0.9, 1, null],
        [1, 1, null],
    ],
    scaleY: [
        [0, 0.92, IN],
        [0.45, 0.86, STANDARD],
        [0.62, 1, IN],
        [0.78, 0.94, STANDARD],
        [0.9, 1, null],
        [1, 1, null],
    ],
    opacity: [
        [0, 0, null],
        [0.18, 1, null],
        [1, 1, null],
    ],
};

const clamp01 = (t: number): number => Math.min(Math.max(t, 0), 1);
const easeInOutSine = (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2;

/** Newton's method against the curve's x, which is how a browser resolves a `cubic-bezier` too. */
function cubicBezier([p1x, p1y, p2x, p2y]: Curve): (x: number) => number {
    const a = (x1: number, x2: number): number => 1 - 3 * x2 + 3 * x1;
    const b = (x1: number, x2: number): number => 3 * x2 - 6 * x1;
    const c = (x1: number): number => 3 * x1;
    const at = (t: number, x1: number, x2: number): number => ((a(x1, x2) * t + b(x1, x2)) * t + c(x1)) * t;
    const slope = (t: number, x1: number, x2: number): number => 3 * a(x1, x2) * t * t + 2 * b(x1, x2) * t + c(x1);

    return (x: number): number => {
        if (x <= 0) {
            return 0;
        }
        if (x >= 1) {
            return 1;
        }

        let t = x;
        for (let i = 0; i < 6; i++) {
            const gradient = slope(t, p1x, p2x);
            if (gradient === 0) {
                break;
            }
            t -= (at(t, p1x, p2x) - x) / gradient;
        }
        return at(t, p1y, p2y);
    };
}

/* Solving a curve costs more than reading it, and there are only two of them across six marks and every
   frame, so each is solved once. */
const solved = new Map<string, (x: number) => number>();

function ease(curve: Curve | null, t: number): number {
    /* A keyframe with no curve of its own inherits the CSS default, which smoothstep is within a pixel of. */
    if (!curve) {
        return t * t * (3 - 2 * t);
    }

    const key = curve.join();
    let solve = solved.get(key);
    if (!solve) {
        solve = cubicBezier(curve);
        solved.set(key, solve);
    }
    return solve(t);
}

/** Reads one track at a point in the drop, easing between the keyframes it falls between. */
function readTrack(keys: Keyframe[], t: number): number {
    for (let i = keys.length - 1; i >= 0; i--) {
        const [at, value, curve] = keys[i];
        if (t < at && i > 0) {
            continue;
        }

        const next = keys[i + 1];
        if (!next) {
            return value;
        }

        const span = next[0] - at;
        const progress = span > 0 ? ease(curve, Math.min((t - at) / span, 1)) : 1;
        return value + (next[1] - value) * progress;
    }
    return keys[0][1];
}

const markEls = useTemplateRef<HTMLElement[]>("markEls");

/** Pure in `elapsed`, so it renders the same however coarsely the clock is ticking. */
function draw(elapsed: number): void {
    markEls.value?.forEach((el, index) => {
        const dropAt = clamp01((elapsed - DROP_STAGGER * index) / DROP_MS);

        /* The wave only starts once every mark has landed, so the row turns as one thing rather than each
           mark chasing its own drop. */
        let turn = 0;
        const sinceWave = elapsed - WAVE_STARTS;
        if (sinceWave > 0) {
            const within = (sinceWave % WAVE_CYCLE) - SPIN_STAGGER * index;
            if (within > 0 && within < SPIN_MS) {
                turn = easeInOutSine(within / SPIN_MS) * 360;
            }
        }

        const y = readTrack(TRACKS.y, dropAt);
        const scaleX = readTrack(TRACKS.scaleX, dropAt);
        const scaleY = readTrack(TRACKS.scaleY, dropAt);

        el.style.opacity = readTrack(TRACKS.opacity, dropAt).toFixed(3);
        el.style.transform = `translateY(${y.toFixed(2)}px) rotate(${turn.toFixed(1)}deg) scale(${scaleX.toFixed(3)}, ${scaleY.toFixed(3)})`;
    });
}

let frame: number | null = null;

onMounted(() => {
    /* Reduced motion keeps the stylesheet's resting row: the six marks, landed, which is the still frame the
       animation is made of. */
    if (globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
    }

    /* Nothing is painted here. The stylesheet leaves the marks landed and visible, and the first frame — which
       runs before the browser paints — hides them for the drop. A loader that hid them up front would leave an
       empty strip on screen for as long as the first frame took to arrive, or forever if it never did. */
    const start = performance.now();
    const tick = (now: number): void => {
        draw(now - start);
        frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
});

onBeforeUnmount(() => {
    if (frame !== null) {
        cancelAnimationFrame(frame);
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* Six marks at their full size need 360px, which is wider than the screen leaves on a 320px or 375px
       phone once the step's padding is taken off. The row shrinks rather than wrapping, so the six stay one
       line reading left to right, which is what the drop and the wave are. */
    .marks {
        @apply flex items-center gap-3 sm:gap-6;
    }

    /* Landed and visible unless the driver says otherwise, so a frame that never arrives leaves the profile's
       marks on screen rather than nothing. */
    .mark {
        @apply block size-8 sm:size-10;
    }

    /* The marks are drawn at list-marker size; the loader shows them at their own. */
    .mark :deep(svg) {
        @apply block size-full;
    }
}
</style>
